// Admin inquiry detail. Full read-only view of one submitted request: contact
// info, area stats, the preliminary estimate, the product/option selection
// snapshot (as submitted, so it stays accurate even if the catalog later
// changes), the customer's note, and status/timestamps.
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link as RouterLink } from 'react-router';
import {
  Box, Typography, Alert, CircularProgress, Chip, Button, Paper, Divider,
  List, ListItem, ListItemText, Link, Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { fetchInquiry, clearDetail } from '../../store/admin-inquiries-slice.js';
import { selectSettings } from '../../store/settings-slice.js';
import LocationHighlightSvg from '../../components/location-highlight/LocationHighlightSvg.jsx';
import {
  estimateText, areaText, populationText, formatDateTime, statusColor, areaTypeLabel,
} from '../../utils/admin-inquiry-format.js';

const Field = ({ label, children }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="body1">{children}</Typography>
  </Box>
);

const Section = ({ title, children }) => (
  <Paper variant="outlined" sx={{ p: 3 }}>
    <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

const AdminInquiryDetailPage = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { detail, detailLoading, detailError } = useSelector((s) => s.adminInquiries);
  const settings = useSelector(selectSettings);

  useEffect(() => {
    dispatch(fetchInquiry(id));
    return () => { dispatch(clearDetail()); };
  }, [dispatch, id]);

  const backButton = (
    <Button
      component={RouterLink}
      to="/admin/inquiries"
      startIcon={<ArrowBackIcon />}
      sx={{ mb: 2 }}
    >
      Back to inquiries
    </Button>
  );

  if (detailLoading) {
    return (
      <Box>
        {backButton}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress aria-label="Loading inquiry" />
        </Box>
      </Box>
    );
  }

  if (detailError) {
    return (
      <Box>
        {backButton}
        <Alert severity="error">{detailError}</Alert>
      </Box>
    );
  }

  if (!detail) return <Box>{backButton}</Box>;

  const selections = Array.isArray(detail.selections_json) ? detail.selections_json : [];
  const areaLabel = detail.area_name
    ? `${detail.area_name} (${areaTypeLabel(detail.area_type)})`
    : areaTypeLabel(detail.area_type);
  const geometry = detail.geometry || {};
  const hasGeometry = Boolean(
    geometry.countyFips || geometry.displayGeoJson || geometry.tileGeoJson,
  );

  return (
    <Box>
      {backButton}

      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap',
      }}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          {`Inquiry #${detail.id}`}
        </Typography>
        <Chip label={detail.status} color={statusColor(detail.status)} />
      </Box>

      <Box sx={{
        display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      }}
      >
        <Section title="Contact">
          <Stack spacing={2}>
            <Field label="Name">{detail.contact_name}</Field>
            <Field label="Email">
              <Link href={`mailto:${detail.contact_email}`}>{detail.contact_email}</Link>
            </Field>
            <Field label="Organization">{detail.contact_org}</Field>
            <Field label="Role">{detail.contact_role || '—'}</Field>
          </Stack>
        </Section>

        <Section title="Area & estimate">
          <Stack spacing={2}>
            <Field label="Area">{areaLabel}</Field>
            <Field label="Size">{areaText(detail.area_sq_mi)}</Field>
            <Field label="Estimated population">{populationText(detail.est_population)}</Field>
            <Field label="Preliminary estimate">
              {estimateText(detail.estimated_low, detail.estimated_high)}
            </Field>
          </Stack>
        </Section>

        <Section title="Selected products">
          {selections.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No products selected.</Typography>
          ) : (
            <List disablePadding>
              {selections.map((sel) => (
                <ListItem key={sel.productId} disableGutters sx={{ display: 'block', py: 0.5 }}>
                  <ListItemText
                    primary={(
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                        {sel.name}
                        {sel.customQuote && (
                          <Chip label="Custom quote" size="small" color="primary" variant="outlined" />
                        )}
                      </Box>
                    )}
                    secondary={
                      Array.isArray(sel.options) && sel.options.length > 0
                        ? sel.options
                          .map((o) => `${o.name}${o.customQuote ? ' (custom quote)' : ''}`)
                          .join(', ')
                        : null
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Section>

        <Section title="Additional details">
          {detail.details_text ? (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {detail.details_text}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No additional details provided.
            </Typography>
          )}
        </Section>
      </Box>

      {hasGeometry && (
        <Box sx={{ mt: 3 }}>
          <Section title="Area map">
            <LocationHighlightSvg
              areaType={geometry.areaType}
              countyFips={geometry.countyFips}
              displayGeoJson={geometry.displayGeoJson}
              tileGeoJson={geometry.tileGeoJson}
              name={detail.area_name}
              regionName={settings.region_display_name}
            />
          </Section>
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={3}
        sx={{ color: 'text.secondary' }}
      >
        <Field label="Received">{formatDateTime(detail.created_at)}</Field>
        <Field label="Submitted">{formatDateTime(detail.submitted_at)}</Field>
        <Field label="Emailed">{formatDateTime(detail.emailed_at)}</Field>
      </Stack>
    </Box>
  );
};

export default AdminInquiryDetailPage;
